const VIS_KEYS = new Set(["term", "targets", "sources"]);

/**
 * Strip vue-degree-plan back-references before JSON.stringify / S3 upload.
 * Keeps prereq/coreq trees on courses; only removes visualization-only links.
 */
export function cleanPlanForSave(terms: any[]): any[] {
  return JSON.parse(
    JSON.stringify(terms, (key, value) => {
      if (VIS_KEYS.has(key) && typeof value === "object" && value !== null) {
        return undefined;
      }
      return value;
    })
  );
}

function djb2PairHex(str: string): string {
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = ((h1 << 5) + h1) ^ c;
    h2 = ((h2 << 5) + h2) ^ c;
  }
  const a = (h1 >>> 0).toString(16).padStart(8, "0");
  const b = (h2 >>> 0).toString(16).padStart(8, "0");
  return `${a}${b}`;
}

/** Normalized course code (aligned with Transfer.vue slimTakenCourse). */
export function normalizeCourseCodeForFingerprint(raw: any): string {
  const s = String(raw ?? "")
    .toUpperCase()
    .trim()
    .replace(/\s+/g, " ");
  const m = s.match(/^([A-Z]+)\s*[- ]?\s*(\d.*)$/);
  if (m) return `${m[1]} ${m[2]}`.replace(/\s+/g, " ").trim();
  return s;
}

/**
 * Order-independent fingerprint of completed courses for unique S3 keys / plan names.
 */
export function hashCompletedCourseSet(courses: any[]): string {
  const parts = (courses || []).map((c) => {
    const rawCode =
      c?.code ??
      (c?.prefix != null && c?.num != null ? `${c.prefix} ${c.num}` : "");
    const code = normalizeCourseCodeForFingerprint(rawCode);
    const id = Number(c?.id ?? 0);
    const idPart = Number.isFinite(id) ? id : 0;
    return `${idPart}:${code}`;
  });
  parts.sort();
  return djb2PairHex(parts.join("|"));
}

/** Avoid invalid S3 keys from program / school names */
export function sanitizeUploadLabel(s: string): string {
  return String(s || "")
    .replace(/[/\\?*:|"<>\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 180) || "plan";
}

/** Rebuild stable S3 path query params after load (persisted slug preferred). */
export function resolveS3SlugFromPlan(plan: any): { dest: string; start: string } {
  if (plan?.s3Slug?.dest && plan?.s3Slug?.start) {
    return { dest: plan.s3Slug.dest, start: plan.s3Slug.start };
  }
  const dest = sanitizeUploadLabel(plan?.destinationProgram?.name || "Destination");
  if (plan?.planType === "completion") {
    const school = plan?.startingSchool?.name || "CC";
    const hash = plan?.courseSetHash;
    if (hash) {
      return {
        dest,
        start: sanitizeUploadLabel(`Eval-${school}-${hash}`),
      };
    }
    return {
      dest,
      start: sanitizeUploadLabel(`Eval-${school}`),
    };
  }
  return {
    dest,
    start: sanitizeUploadLabel(plan?.startingProgram?.name || "Source"),
  };
}
